/** React Query hooks over the office service. */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { officeService } from "../api";
import type { AnalyticsQuery, LeaveType, ManualStatus, OrgSettings } from "../api/types";

const KEY = "office";

// ---------------------------------------------------------------------- me

export function useMe() {
  return useQuery({
    queryKey: [KEY, "me"],
    queryFn: () => officeService.getMe(),
    staleTime: 60_000,
    // Everything in the module hangs off this one call, and its failure is
    // rendered rather than swallowed, so surface it quickly instead of sitting
    // through the default retry schedule looking like nothing is wrong.
    retry: 1,
  });
}

export function useSetStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ status, message }: { status: ManualStatus; message?: string }) =>
      officeService.setStatus(status, message),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "me"] });
      void qc.invalidateQueries({ queryKey: [KEY, "timer"] });
    },
  });
}

export function useTimer(enabled = true) {
  return useQuery({
    queryKey: [KEY, "timer"],
    queryFn: () => officeService.getTimer(),
    // The live counter ticks client-side between polls; this only corrects
    // drift and picks up a status change made in another tab.
    refetchInterval: 60_000,
    enabled,
  });
}

export function usePermissions(): Set<string> {
  const { data } = useMe();
  return new Set(data?.permissions ?? []);
}

// ----------------------------------------------------------------- the floor

export function useMap(mapId?: string) {
  return useQuery({
    queryKey: [KEY, "map", mapId ?? "current"],
    queryFn: () => officeService.getMap(mapId),
    staleTime: 5 * 60_000,
  });
}

/**
 * Every floor's plan at once, for the building view.
 *
 * `useQueries` rather than a loop of `useMap`, because the number of floors is
 * data and hooks cannot be called in a loop whose length changes. Shares
 * `useMap`'s cache key, so opening a floor afterwards is already loaded.
 */
export function useFloorLayouts(floorIds: string[]) {
  return useQueries({
    queries: floorIds.map((id) => ({
      queryKey: [KEY, "map", id],
      queryFn: () => officeService.getMap(id),
      staleTime: 5 * 60_000,
    })),
  });
}

export function useFloors(organizationId?: string) {
  return useQuery({
    queryKey: [KEY, "floors", organizationId ?? "mine"],
    queryFn: () => officeService.listFloors(organizationId),
    // Headcounts move as people walk about; this is a picker, not a live board.
    refetchInterval: 60_000,
  });
}

/** Every organization, for the super-admin floor manager. Nobody else may ask. */
export function useOrganizations(enabled: boolean) {
  return useQuery({
    queryKey: [KEY, "organizations"],
    queryFn: () => officeService.listOrganizations(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

function invalidateFloors(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: [KEY, "floors"] });
  void qc.invalidateQueries({ queryKey: [KEY, "map"] });
  void qc.invalidateQueries({ queryKey: [KEY, "desks"] });
}

export function useSwitchFloor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mapId: string) => officeService.switchFloor(mapId),
    onSuccess: () => {
      invalidateFloors(qc);
      void qc.invalidateQueries({ queryKey: [KEY, "me"] });
      void qc.invalidateQueries({ queryKey: [KEY, "presence"] });
    },
  });
}

export function useCreateFloor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof officeService.createFloor>[0]) =>
      officeService.createFloor(body),
    onSuccess: () => invalidateFloors(qc),
  });
}

export function useUpdateFloor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      officeService.updateFloor(id, body),
    onSuccess: () => invalidateFloors(qc),
  });
}

export function useDeleteFloor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => officeService.deleteFloor(id),
    onSuccess: () => invalidateFloors(qc),
  });
}

export function usePresenceList(
  opts: { includeOffline?: boolean; team?: string; scope?: "org" } = {},
) {
  return useQuery({
    queryKey: [KEY, "presence", opts],
    queryFn: () => officeService.getPresence(opts),
    refetchInterval: 30_000,
  });
}

// ------------------------------------------------------------------ directory

export function useDirectory(opts: { team?: string; search?: string } = {}) {
  return useQuery({
    queryKey: [KEY, "users", opts],
    queryFn: () => officeService.listUsers(opts),
  });
}

export function useTeams() {
  return useQuery({ queryKey: [KEY, "teams"], queryFn: () => officeService.listTeams() });
}

// ---------------------------------------------------------------------- leave

export function useMyBalances(year?: number) {
  return useQuery({
    queryKey: [KEY, "balances", year ?? "current"],
    queryFn: () => officeService.getMyBalances(year),
  });
}

export function useLeaveRequests(opts: Parameters<typeof officeService.listLeaveRequests>[0] = {}) {
  return useQuery({
    queryKey: [KEY, "leave", opts],
    queryFn: () => officeService.listLeaveRequests(opts),
  });
}

export function useLeavePolicies() {
  return useQuery({
    queryKey: [KEY, "leave-policies"],
    queryFn: () => officeService.listLeavePolicies(),
  });
}

function invalidateLeave(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: [KEY, "leave"] });
  void qc.invalidateQueries({ queryKey: [KEY, "balances"] });
  void qc.invalidateQueries({ queryKey: [KEY, "leave-calendar"] });
}

export function useSubmitLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      leave_type: LeaveType;
      start_date: string;
      end_date: string;
      day_part?: string;
      note?: string;
    }) => officeService.submitLeave(body),
    onSuccess: () => invalidateLeave(qc),
  });
}

export function useDecideLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      note,
    }: {
      id: string;
      decision: "approved" | "rejected";
      note?: string;
    }) => officeService.decideLeave(id, decision, note),
    onSuccess: () => invalidateLeave(qc),
  });
}

export function useCancelLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => officeService.cancelLeave(id),
    onSuccess: () => invalidateLeave(qc),
  });
}

export function useLeaveCalendar(opts: Parameters<typeof officeService.getLeaveCalendar>[0] = {}) {
  return useQuery({
    queryKey: [KEY, "leave-calendar", opts],
    queryFn: () => officeService.getLeaveCalendar(opts),
  });
}

// -------------------------------------------------------------------- reports

export function useDailyReports(opts: Parameters<typeof officeService.getDailyReports>[0] = {}) {
  return useQuery({
    queryKey: [KEY, "reports", "daily", opts],
    queryFn: () => officeService.getDailyReports(opts),
  });
}

export function useRollup(period: "weekly" | "monthly", count?: number, userId?: string) {
  return useQuery({
    queryKey: [KEY, "reports", period, count, userId],
    queryFn: () => officeService.getRollup(period, count, userId),
  });
}

export function useLiveBoard(enabled = true) {
  return useQuery({
    queryKey: [KEY, "reports", "live"],
    queryFn: () => officeService.getLiveBoard(),
    refetchInterval: 30_000,
    enabled,
  });
}

export function useTeamBoard(enabled = true) {
  return useQuery({
    queryKey: [KEY, "reports", "team"],
    queryFn: () => officeService.getTeamBoard(),
    refetchInterval: 30_000,
    enabled,
  });
}

export function useOrgMetrics(enabled = true) {
  return useQuery({
    queryKey: [KEY, "reports", "metrics"],
    queryFn: () => officeService.getOrgMetrics(),
    enabled,
  });
}

export function useSaveDayNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ date, note }: { date: string; note: string }) =>
      officeService.saveDayNote(date, note),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [KEY, "reports", "daily"] }),
  });
}

// --------------------------------------------------------------------- social

export function useKnocks() {
  return useQuery({
    queryKey: [KEY, "knocks"],
    queryFn: () => officeService.listKnocks(),
    refetchInterval: 30_000,
  });
}

export function useSendKnock() {
  return useMutation({
    mutationFn: ({
      toUserId,
      intent,
      reason,
    }: {
      toUserId: string;
      intent: "chat" | "call";
      reason?: string;
    }) => officeService.sendKnock(toUserId, intent, reason),
  });
}

export function useAnswerKnock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accepted }: { id: string; accepted: boolean }) =>
      officeService.answerKnock(id, accepted),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [KEY, "knocks"] }),
  });
}

export function useAnnouncements() {
  return useQuery({
    queryKey: [KEY, "announcements"],
    queryFn: () => officeService.listAnnouncements(),
  });
}

export function useSendAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof officeService.sendAnnouncement>[0]) =>
      officeService.sendAnnouncement(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [KEY, "announcements"] }),
  });
}

export function useAckAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => officeService.ackAnnouncement(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [KEY, "announcements"] }),
  });
}

// ---------------------------------------------------------------------- tasks

export function useTasks(opts: { repo?: string; state?: string; q?: string } = {}) {
  return useQuery({
    queryKey: [KEY, "tasks", opts],
    queryFn: () => officeService.listTasks(opts),
  });
}

export function useSetFocusTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => officeService.focusTask(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "tasks"] });
      void qc.invalidateQueries({ queryKey: [KEY, "me"] });
    },
  });
}

export function useClearFocusTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => officeService.clearFocus(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "tasks"] });
      void qc.invalidateQueries({ queryKey: [KEY, "me"] });
    },
  });
}

export function useSetManualFocus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (title: string) => officeService.setManualFocus(title),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "tasks"] });
      void qc.invalidateQueries({ queryKey: [KEY, "me"] });
    },
  });
}

// ---------------------------------------------------------------------- admin

export function useOrgSettings(enabled = true) {
  return useQuery({
    queryKey: [KEY, "settings"],
    queryFn: () => officeService.getSettings(),
    enabled,
  });
}

export function useUpdateOrgSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<OrgSettings>) => officeService.updateSettings(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [KEY, "settings"] }),
  });
}

export function useRoleAssignments(enabled = true) {
  return useQuery({
    queryKey: [KEY, "roles"],
    queryFn: () => officeService.getRoleAssignments(),
    enabled,
  });
}

export function useSetRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role, grant }: { userId: string; role: string; grant: boolean }) =>
      officeService.setRole(userId, role, grant),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [KEY, "roles"] }),
  });
}

// ------------------------------------------------------------------ analytics

export function useAnalyticsDashboard(q: AnalyticsQuery) {
  return useQuery({
    queryKey: [KEY, "analytics", "dashboard", q],
    queryFn: () => officeService.analytics.dashboard(q),
  });
}

function analyticsHook<T>(name: string, call: (q: AnalyticsQuery) => Promise<T>) {
  return (q: AnalyticsQuery, enabled = true) =>
    useQuery({ queryKey: [KEY, "analytics", name, q], queryFn: () => call(q), enabled });
}

export const useAttendance = analyticsHook("attendance", officeService.analytics.attendance);
export const useStatusComposition = analyticsHook(
  "composition",
  officeService.analytics.statusComposition,
);
export const useUtilization = analyticsHook("utilization", officeService.analytics.utilization);
// Analytics rollup (team/role group-by); distinct from the reports useRollup above.
export const useAnalyticsRollup = analyticsHook("rollup", officeService.analytics.rollup);
export const useTasksLoad = analyticsHook("tasks", officeService.analytics.tasks);
export const useLeaveCoverage = analyticsHook("leave", officeService.analytics.leave);
export const useOccupancy = analyticsHook("occupancy", officeService.analytics.occupancy);
export const useInsights = analyticsHook("insights", officeService.analytics.insights);
